import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building, Users, Briefcase, Globe, UserPlus, Clock, CheckCircle,
  Calendar, FileText, TrendingUp, AlertCircle, ArrowRight, Plus,
  ClipboardList, UserCheck, Timer, Bell, Palmtree, DollarSign,
  Target, CalendarDays, LogIn, LogOut, Wallet, ListChecks, Sun,
  Newspaper, Megaphone, Pin, AlertTriangle, Award, Activity,
  Coffee, Zap, BarChart3, PieChart, TrendingDown, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import DashboardDigest from '../components/DashboardDigest';

// Employee Dashboard Component
const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, currentOffice, getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [news, setNews] = useState([]);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  // Re-fetch after check-in completes
  useEffect(() => {
    const handler = () => fetchEmployeeData();
    window.addEventListener('checkin-complete', handler);
    return () => window.removeEventListener('checkin-complete', handler);
  }, []);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      const responses = await Promise.all([
        fetch(`${API_URL}/api/attendance/my-status`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/leave/my-balance`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/tasks/my-tasks?limit=5`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/employees/me`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/news/dashboard?limit=3`, { headers }).catch(() => null)
      ]);

      const [attendanceRes, leaveRes, tasksRes, profileRes, newsRes] = responses;

      if (attendanceRes?.ok) setAttendance(await attendanceRes.json());
      if (leaveRes?.ok) setLeaveBalance(await leaveRes.json());
      if (tasksRes?.ok) setTasks(await tasksRes.json());
      if (profileRes?.ok) setEmployeeProfile(await profileRes.json());
      if (newsRes?.ok) setNews(await newsRes.json());
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  // Helper function for Title Case
  const toTitleCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  };

  const firstName = toTitleCase(employeeProfile?.first_name || user?.first_name || 'Employee');
  const pendingTasks = tasks.filter(t => t.status === 'To Do' || t.status === 'In Progress').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
          <p className="text-gray-500">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Dashboard Digest - Today at a Glance */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <DashboardDigest />
      </motion.div>

      {/* Quick Links & My Tasks Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Links */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
          <Card className="shadow-lg h-full">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="h-5 w-5 text-amber-500" />
                Quick Links
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="h-20 flex-col gap-2 hover:bg-green-50 hover:border-green-200" onClick={() => navigate('/my-leave')}>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Palmtree className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-sm">Request Leave</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2 hover:bg-purple-50 hover:border-purple-200" onClick={() => navigate('/my-tasks')}>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Plus className="h-5 w-5 text-purple-600" />
                  </div>
                  <span className="text-sm">Create Task</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2 hover:bg-blue-50 hover:border-blue-200" onClick={() => navigate('/attendance')}>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="text-sm">Attendance</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2 hover:bg-emerald-50 hover:border-emerald-200" onClick={() => navigate('/my-salary')}>
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                  <span className="text-sm">My Payslips</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2 hover:bg-indigo-50 hover:border-indigo-200" onClick={() => navigate('/my-calendar')}>
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Calendar className="h-5 w-5 text-indigo-600" />
                  </div>
                  <span className="text-sm">My Calendar</span>
                </Button>
                <Button variant="outline" className="h-20 flex-col gap-2 hover:bg-pink-50 hover:border-pink-200" onClick={() => navigate('/profile')}>
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <UserCheck className="h-5 w-5 text-pink-600" />
                  </div>
                  <span className="text-sm">My Profile</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* My Tasks */}
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
          <Card className="shadow-lg h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ListChecks className="h-5 w-5 text-purple-600" />
                  My Tasks
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/my-tasks')} className="text-purple-600 hover:text-purple-700">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {tasks.length > 0 ? (
                <div className="space-y-3">
                  {tasks.slice(0, 5).map((task, idx) => (
                    <div key={task.id || idx} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => navigate('/my-tasks')}>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          task.priority === 'Urgent' ? 'bg-red-500' :
                          task.priority === 'High' ? 'bg-orange-500' :
                          task.priority === 'Medium' ? 'bg-blue-500' : 'bg-gray-400'
                        }`}></div>
                        <div>
                          <p className="font-medium text-sm">{task.title}</p>
                          <p className="text-xs text-gray-500">{task.due_date ? `Due: ${new Date(task.due_date).toLocaleDateString()}` : 'No due date'}</p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {task.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ListChecks className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No tasks assigned</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/my-tasks')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Task
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

// Office Admin Dashboard
const OfficeAdminDashboard = () => {
  // Import and use the new AnalyticsDashboard
  const AnalyticsDashboard = React.lazy(() => import('./AnalyticsDashboard'));
  
  return (
    <div className="space-y-6">
      <DashboardDigest />
      <React.Suspense fallback={
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
            <p className="text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      }>
        <AnalyticsDashboard />
      </React.Suspense>
    </div>
  );
};

// Super Admin Dashboard
const SuperAdminDashboard = () => {
  // Import and use the new SuperAdminAnalyticsDashboard
  const SuperAdminAnalyticsDashboard = React.lazy(() => import('./SuperAdminAnalyticsDashboard'));
  
  return (
    <React.Suspense fallback={
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600"></div>
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    }>
      <SuperAdminAnalyticsDashboard />
    </React.Suspense>
  );
};

// Main Overview Component
const Overview = () => {
  const { isOfficeAdmin, isSuperAdmin } = useAuth();

  if (isSuperAdmin()) {
    return <SuperAdminDashboard />;
  }

  if (isOfficeAdmin()) {
    return <OfficeAdminDashboard />;
  }

  return <EmployeeDashboard />;
};

export default Overview;
